// Iter118 — Select-tier inventory countdown badge state.
//
// Two-stage:
//   ≤ lowStockThreshold      → { level: "low",     label: "X vials left" }
//   ≤ limitedStockThreshold  → { level: "limited", label: "Limited Stock" }
//   above / untracked         → null (no badge)
//
// For multi-variant products, pass the LOWEST variant vials_in_stock.
// Untracked products (vials_in_stock === null or undefined) return null.

export function selectStockBadge(vialsInStock, {
  lowStockThreshold = 20,
  limitedStockThreshold = 30,
  mgLabel = null,
} = {}) {
  if (vialsInStock === null || vialsInStock === undefined) return null;
  const n = Number(vialsInStock);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n <= lowStockThreshold) {
    const label = mgLabel ? `${n} vials left · ${mgLabel}` : `${n} vials left`;
    return { level: "low", label, count: n };
  }
  if (n <= limitedStockThreshold) {
    return { level: "limited", label: "Limited Stock", count: n };
  }
  return null;
}

// Pick the lowest tracked vials_in_stock across a product's variants + product-level.
// Returns { count, mgLabel } for the tightest strength, or null if none tracked.
export function lowestTrackedVials(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const trackedVariants = variants
    .filter((v) => v && v.vials_in_stock !== null && v.vials_in_stock !== undefined && v.in_stock !== false);
  if (trackedVariants.length > 0) {
    // Sort ascending by vials_in_stock, pick the lowest
    const sorted = [...trackedVariants].sort((a, b) => Number(a.vials_in_stock) - Number(b.vials_in_stock));
    const v = sorted[0];
    const mg = String(v.mg || "").trim();
    return {
      count: Number(v.vials_in_stock),
      mgLabel: mg ? `${mg}${mg.toLowerCase().endsWith("mg") ? "" : "mg"}` : null,
    };
  }
  if (product?.vials_in_stock !== null && product?.vials_in_stock !== undefined) {
    return { count: Number(product.vials_in_stock), mgLabel: null };
  }
  return null;
}

// iter204.4 — Total Actual Stock for a product across its tier's inventory.
// For multi-variant products, sums every tracked variant's `vials_in_stock`.
// For flat products, uses the product-level `vials_in_stock` (which the
// backend keeps as sum of that product's active lots on its tier).
// Returns null when nothing is tracked (so callers can hide the display
// entirely instead of showing 0 for untracked legacy rows).
export function totalActualStock(product) {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  const tracked = variants.filter(
    (v) => v && v.vials_in_stock !== null && v.vials_in_stock !== undefined,
  );
  if (tracked.length > 0) {
    return tracked.reduce((sum, v) => sum + Number(v.vials_in_stock || 0), 0);
  }
  if (product?.vials_in_stock !== null && product?.vials_in_stock !== undefined) {
    return Number(product.vials_in_stock);
  }
  return null;
}
